import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  CardBody,
  Badge,
  Button,
  Breadcrumb,
  BreadcrumbItem,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from 'reactstrap';
import classnames from 'classnames';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import ConfirmModal from '../../../../Components/crude/ConfirmModal';
import RequirementTab from './tabs/RequirementTab';
import SupportingDocumentTab from './tabs/SupportingDocumentTab';
import DmutInvitationTab from './tabs/DmutInvitationTab';
import OverviewTab from './tabs/OverviewTab';
import FloatingActions from './tabs/FloatingActions';
import * as api from './planningApi';
import { VALIDATORS } from './validation';
import { STATUS, STATUS_BADGE, STEP, STEP_STATUS } from './constants';
import { formatDateTime } from '../formatters';

const TABS = [
  {
    id: 1,
    icon: 'ri-list-ordered',
    label: 'REQUIREMENT',
    step: STEP.REQUIREMENT,
  },
  {
    id: 2,
    icon: 'ri-file-text-line',
    label: 'SUPPORTING DOCUMENT',
    step: STEP.SUPPORTING_DOCUMENT,
  },
  {
    id: 3,
    icon: 'ri-mail-send-line',
    label: 'DMUT INVITATION',
    step: STEP.DMUT,
  },
  { id: 4, icon: 'ri-checkbox-circle-line', label: 'OVERVIEW', step: null },
];

// Create-continuation / Edit / Detail (read-only) share this shell. `readOnly`
// is forced on for submitted records regardless of the route used to reach them.
const PlanningForm = ({ readOnly: readOnlyProp = false }) => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [record, setRecord] = useState(null);
  const [detail, setDetail] = useState(null);
  const [activeTab, setActiveTab] = useState(1);
  const [submitModal, setSubmitModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notFound, setNotFound] = useState(false);

  const loadDetail = useCallback(async () => {
    try {
      const { record: rec, detail: det } = await api.getPlanning(id);
      setRecord(rec);
      setDetail(det);
    } catch (error) {
      toast.error(`Failed to load planning: ${error}`);
      setNotFound(true);
    }
  }, [id]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  // Tabs mutate server state immediately (add/delete cargo, crude, documents);
  // they call this to pull fresh server truth and merge just the collection(s)
  // they own, without clobbering unsaved local edits on other tabs.
  const refresh = useCallback(async () => {
    const { detail: det } = await api.getPlanning(id);
    return det;
  }, [id]);

  // Hard Recall reopens a submitted Planning for revision. The 13 mutation
  // endpoints unlock; Delete stays locked and the Contract fields are frozen
  // (the server ignores them rather than erroring — contract section 6, changed
  // 2026-07-23). The contract asks the FE to gate on this flag rather than
  // waiting for a 422.
  const isHardRecall = !!record?.isHardRecall;

  // A recalled record must stay editable even though `phaseStatusName` reports
  // the Procurement header's status, which by then belongs to a later stage
  // ('On Hard Recall', 'Submitted', ...) rather than to Planning itself — see
  // STATUS in constants.js.
  // Planning dianggap SUBMITTED bila header eksplisit 'Submitted' ATAU fase
  // header sudah melewati 'Planning' (mis. 'Procurement' berstatus Draft —
  // Draft itu milik fase baru, bukan Planning).
  const planningSubmitted =
    record?.status === STATUS.SUBMITTED ||
    (record?.mainStatus && record.mainStatus !== 'Planning');

  // Status yang DITAMPILKAN untuk Planning ini (badge header).
  const displayStatus = planningSubmitted ? STATUS.SUBMITTED : record?.status;

  const readOnly = readOnlyProp || (planningSubmitted && !isHardRecall);

  const allStepsCompleted = useMemo(() => {
    if (!detail) return false;
    return [STEP.REQUIREMENT, STEP.SUPPORTING_DOCUMENT, STEP.DMUT].every(
      k => detail.steps[k] === STEP_STATUS.COMPLETED
    );
  }, [detail]);

  document.title = record ? `Planning - ${record.procId}` : 'Planning';

  const currentStep = TABS.find(t => t.id === activeTab)?.step;

  // ---- Per-tab persistence -------------------------------------------------
  // Each editable tab maps to its own endpoint. `completed` decides InProgress
  // (Save as Draft) vs Completed (Save & Completed) — the latter runs the tab's
  // client-side validator first, then the server enforces the same rules.
  const saveActiveTab = async completed => {
    if (!currentStep) return;
    const stepStatus = completed
      ? STEP_STATUS.COMPLETED
      : STEP_STATUS.IN_PROGRESS;

    if (completed) {
      const errors = VALIDATORS[currentStep](detail);
      if (errors.length) {
        toast.error(
          <div>
            Please complete the required fields:
            <ul className='mb-0 mt-1' style={{ paddingLeft: '1.25rem' }}>
              {errors.map((e, i) => (
                <li key={i}>{e}</li>
              ))}
            </ul>
          </div>,
          { autoClose: 6000 }
        );
        return;
      }
    }

    setSaving(true);
    try {
      if (currentStep === STEP.REQUIREMENT) {
        const r = detail.requirement;
        await api.saveMainRequirement(id, {
          stepStatus,
          contractType: r.contractType,
          startPeriod: r.startPeriod,
          endPeriod: r.endPeriod,
          clauses: r.clauses,
          additionalNotes: r.additionalNotes,
          cargoNotes: r.cargoNotes,
        });
      } else if (currentStep === STEP.SUPPORTING_DOCUMENT) {
        await api.saveDocumentStatus(id, stepStatus);
      } else if (currentStep === STEP.DMUT) {
        const invited = (detail.dmut.rows || []).filter(x => x.invited);
        await api.saveDmutInvitation(id, { stepStatus, items: invited });
      }
      await loadDetail();
      toast.success(completed ? 'Saved & marked completed' : 'Saved as draft');
    } catch (error) {
      toast.error(`Save failed: ${error}`);
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      await api.submitPlanning(id);
      setSubmitModal(false);
      toast.success('Planning submitted');
      navigate('/crude/planning');
    } catch (error) {
      toast.error(`Submit failed: ${error}`);
    } finally {
      setSaving(false);
    }
  };

  const goBack = () => setActiveTab(t => Math.max(1, t - 1));

  if (notFound) {
    return (
      <div className='page-content'>
        <Container fluid>
          <div className='text-center py-5'>
            <i className='ri-error-warning-line fs-1 text-muted'></i>
            <h5 className='mt-2'>Planning not found</h5>
            <button
              className='btn btn-primary btn-sm mt-2'
              onClick={() => navigate('/crude/planning')}
            >
              Back to list
            </button>
          </div>
        </Container>
      </div>
    );
  }

  if (!record || !detail) {
    return (
      <div className='page-content'>
        <Container fluid>
          <div className='text-center py-5 text-muted'>Loading...</div>
        </Container>
      </div>
    );
  }

  const isEditableTab = !!currentStep; // tabs 1-3
  const showSaveButtons = !readOnly && isEditableTab;

  // A tab is "done" once we have moved past it (matches ProcurementForm's look).
  const isTabDone = tabId => tabId < activeTab;

  return (
    <React.Fragment>
      <div className='page-content'>
        <Container fluid>
          {isHardRecall && (
            <div className='alert alert-warning d-flex align-items-start'>
              <i className='ri-arrow-go-back-line fs-16 me-2 mt-1'></i>
              <div>
                <strong>This Planning was returned by Hard Recall.</strong> It
                is editable again and must be re-submitted, but the Contract
                section is locked and it cannot be deleted until the recall is
                resolved.
                {/* Reason / who / when come from the Tender Opening log, so any
                    of them can be null even while isHardRecall is true — render
                    each only when the server actually sent it. */}
                {record?.hardRecallReason && (
                  <div className='mt-1'>
                    <span className='fw-semibold'>Reason:</span>{' '}
                    {record.hardRecallReason}
                  </div>
                )}
                {(record?.hardRecallBy || record?.hardRecallAt) && (
                  <div className='text-muted fs-12 mt-1'>
                    Recalled
                    {record.hardRecallBy && ` by ${record.hardRecallBy}`}
                    {record.hardRecallAt &&
                      ` on ${formatDateTime(record.hardRecallAt)}`}
                  </div>
                )}
              </div>
            </div>
          )}

          <div className='email-wrapper d-lg-flex gap-1 mx-n4 mt-3 p-1'>
            <div className='email-content minimal-border'>
              <div className='p-4 pb-0'>
                {/* Header mengikuti gaya /apps-procurement/upd (breadcrumb box +
                    tombol back bundar + judul bold + meta ber-ikon). */}
                <Row className='mt-n2 mb-1 mb-sm-0'>
                  <div className='col-sm order-3 order-sm-2'>
                    <div className='hstack gap-sm-1 align-items-center flex-wrap email-topbar-link'>
                      <h5 className='card-title mb-0 flex-grow-1'>
                        <Breadcrumb listClassName='p-3 py-2 bg-light'>
                          <BreadcrumbItem>
                            <Link to='/dashboard'>Home</Link>
                          </BreadcrumbItem>
                          <BreadcrumbItem>
                            <Link to='/crude/planning'>Planning</Link>
                          </BreadcrumbItem>
                          <BreadcrumbItem active>
                            Detail #{record.procId || '-'}
                          </BreadcrumbItem>
                        </Breadcrumb>
                      </h5>
                    </div>
                  </div>
                </Row>

                <Row className='mb-4'>
                  <Col xs={12}>
                    <Row className='align-items-center g-3'>
                      <Col xs={12} lg>
                        <div className='d-flex align-items-start gap-3'>
                          <Button
                            color='light'
                            className='border shadow-sm rounded-circle d-flex align-items-center justify-content-center p-0'
                            style={{ width: '2.5rem', height: '2.5rem' }}
                            onClick={() => navigate('/crude/planning')}
                          >
                            <i className='bx bx-arrow-back fs-5 text-dark'></i>
                          </Button>
                          <div className='flex-grow-1'>
                            <h4 className='mb-1 fw-bold text-dark'>
                              {record.procurementType} — {record.title || '-'}
                            </h4>
                            <div className='d-flex flex-wrap align-items-center gap-2 text-muted small'>
                              <i className='bx bx-hash'></i>
                              <span>
                                Procurement ID: {record.procId || '-'}
                              </span>
                              <span className='d-none d-md-inline text-secondary opacity-25'>
                                |
                              </span>
                              <span>{record.procurementMethod}</span>
                              <Badge
                                color={
                                  STATUS_BADGE[displayStatus] || 'secondary'
                                }
                                className='fw-semibold px-3 py-1 rounded-pill'
                              >
                                {displayStatus}
                              </Badge>
                            </div>
                          </div>
                          {/* Detail (dtl) route is always read-only — offer a
                              one-click jump to the editable route while the
                              record can still be edited. */}
                          {readOnlyProp &&
                            (!planningSubmitted || isHardRecall) && (
                              <Button
                                color='primary'
                                onClick={() =>
                                  navigate(`/crude/planning/create/${id}`)
                                }
                              >
                                <i className='ri-pencil-line align-bottom me-1'></i>
                                Edit Planning
                              </Button>
                            )}
                        </div>
                      </Col>
                    </Row>
                    <hr className='mt-3 mb-0 border-secondary opacity-10' />
                  </Col>
                </Row>

                <Row className='justify-content-center mb-4'>
                  <Col xxl={12}>
                    <CardBody className='checkout-tab'>
                      <div className='mx-n4 px-4 message-list-scroll'>
                        <Nav
                          tabs
                          className='nav-tabs-custom nav-primary mt-1 mb-3'
                        >
                          {TABS.map(tab => (
                            <NavItem key={tab.id}>
                              <NavLink
                                className={classnames('cursor-pointer', {
                                  active: activeTab === tab.id,
                                  done: isTabDone(tab.id),
                                })}
                                onClick={() => setActiveTab(tab.id)}
                              >
                                <i
                                  className={`${tab.icon} align-middle me-1`}
                                ></i>
                                <span className='fw-semibold'>{tab.label}</span>
                              </NavLink>
                            </NavItem>
                          ))}
                        </Nav>
                      </div>

                      <TabContent activeTab={activeTab}>
                        <TabPane tabId={1}>
                          {activeTab === 1 && (
                            <RequirementTab
                              planningId={id}
                              detail={detail}
                              setDetail={setDetail}
                              refresh={refresh}
                              readOnly={readOnly}
                              isHardRecall={isHardRecall}
                            />
                          )}
                        </TabPane>
                        <TabPane tabId={2}>
                          {activeTab === 2 && (
                            <SupportingDocumentTab
                              planningId={id}
                              detail={detail}
                              setDetail={setDetail}
                              refresh={refresh}
                              readOnly={readOnly}
                            />
                          )}
                        </TabPane>
                        <TabPane tabId={3}>
                          {activeTab === 3 && (
                            <DmutInvitationTab
                              planningId={id}
                              detail={detail}
                              setDetail={setDetail}
                              readOnly={readOnly}
                            />
                          )}
                        </TabPane>
                        <TabPane tabId={4}>
                          {activeTab === 4 && <OverviewTab detail={detail} />}
                        </TabPane>
                      </TabContent>

                      <FloatingActions
                        showBack={activeTab > 1}
                        showSaveDraft={showSaveButtons}
                        showSaveCompleted={showSaveButtons}
                        showSubmit={
                          !readOnly && activeTab === 4 && allStepsCompleted
                        }
                        disabled={saving}
                        onBack={goBack}
                        onSaveDraft={() => saveActiveTab(false)}
                        onSaveCompleted={() => saveActiveTab(true)}
                        onSubmit={() => setSubmitModal(true)}
                      />

                      {!readOnly && activeTab === 4 && !allStepsCompleted && (
                        <div className='text-end text-muted fst-italic mt-2'>
                          Complete all three steps to enable Submit.
                        </div>
                      )}
                    </CardBody>
                  </Col>
                </Row>
              </div>
            </div>
          </div>
        </Container>

        <ConfirmModal
          show={submitModal}
          title='Submit Planning'
          message='Once submitted, this planning can no longer be edited. Continue?'
          confirmLabel='Submit'
          confirmColor='primary'
          onConfirm={handleSubmit}
          onClose={() => setSubmitModal(false)}
        />
        <ToastContainer closeButton={false} limit={1} />
      </div>
    </React.Fragment>
  );
};

export default PlanningForm;
